import React, { useState } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import {
  CheckSquare,
  Square,
  Trash2,
  Plus,
  ClipboardList,
  Clock,
  AlertCircle,
  CheckCircle2,
  Search,
  X,
  Calendar,
  User,
  Tag,
  RefreshCw,
  Loader2
} from 'lucide-react';

export default function Tasks() {
  const { data: tasks, loading } = useStore('tasks');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, done, overdue
  const [priorityFilter, setPriorityFilter] = useState('all'); // all, high, medium, low
  const [categoryFilter, setCategoryFilter] = useState('all'); // all, or specific categories

  // New Task Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('Logistics');
  const [newPriority, setNewPriority] = useState('medium');
  const [newDueDate, setNewDueDate] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());

  const setBusy = (id, busy) => setBusyIds((prev) => {
    const next = new Set(prev);
    busy ? next.add(id) : next.delete(id);
    return next;
  });

  // Categories list for dynamic badges & selectors
  const categories = ['Logistics', 'Catering', 'RSVP', 'Accommodation', 'Budget', 'Marketing', 'Agenda'];

  const notifyChange = () => {
    window.dispatchEvent(new CustomEvent('planner:data-change'));
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || addingTask) return;
    setAddingTask(true);
    const newTask = {
      id: generateId(),
      title: newTitle.trim(),
      category: newCategory,
      priority: newPriority,
      dueDate: newDueDate || new Date().toISOString().split('T')[0],
      assignee: newAssignee.trim() || 'Unassigned',
      status: 'pending',
      updatedAt: new Date().toISOString()
    };
    try {
      await put('tasks', newTask);
      setNewTitle('');
      setNewAssignee('');
      setNewDueDate('');
      setShowAddForm(false);
      notifyChange();
      toast.success('Task created.');
    } catch (err) {
      console.error('Error saving new task:', err);
      toast.error('Failed to create task.');
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleStatus = async (task) => {
    if (busyIds.has(task.id)) return;
    setBusy(task.id, true);
    try {
      await put('tasks', { ...task, status: task.status === 'done' ? 'pending' : 'done', updatedAt: new Date().toISOString() });
      notifyChange();
    } catch (err) {
      console.error('Error toggling task status:', err);
      toast.error('Failed to update task.');
    } finally {
      setBusy(task.id, false);
    }
  };

  const handleDeleteTask = async (id) => {
    const ok = await confirm('Are you sure you want to delete this task?', 'Delete Task');
    if (!ok) return;
    setBusy(id, true);
    try {
      await remove('tasks', id);
      notifyChange();
      toast.success('Task deleted.');
    } catch (err) {
      console.error('Error deleting task:', err);
      toast.error('Failed to delete task.');
    } finally {
      setBusy(id, false);
    }
  };

  const isOverdue = (task) => {
    if (task.status === 'done' || !task.dueDate) return false;
    const taskDate = new Date(task.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    taskDate.setHours(0, 0, 0, 0);
    return taskDate < today;
  };

  // Process and Filter lists
  const processedTasks = tasks.map(t => ({
    ...t,
    isOverdue: isOverdue(t)
  }));

  const filteredTasks = processedTasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          task.assignee.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter === 'pending') matchesStatus = task.status === 'pending' && !task.isOverdue;
    if (statusFilter === 'done') matchesStatus = task.status === 'done';
    if (statusFilter === 'overdue') matchesStatus = task.isOverdue;

    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  // Calculate high-fidelity metrics
  const totalCount = processedTasks.length;
  const completedCount = processedTasks.filter(t => t.status === 'done').length;
  const pendingCount = processedTasks.filter(t => t.status === 'pending' && !t.isOverdue).length;
  const overdueCount = processedTasks.filter(t => t.isOverdue).length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) return <PageLoader label="Loading tasks…" sublabel="Pulling records from the data store" />;

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6">
      
      {/* HEADER CONTROLS */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Task Command Center</h1>
          <p className="text-sm text-slate-500 mt-1">Plan, orchestrate, and audit execution tasks with immediate readiness analytics.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all"
            title="Refresh database state"
          >
            <RefreshCw className="w-3.5 h-3.5 hover:rotate-180 transition-transform duration-500" />
            Sync DB
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 transition-all shadow-sm active:scale-95"
          >
            {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showAddForm ? 'Close Editor' : 'Create Task'}
          </button>
        </div>
      </div>

      {/* SHADCN-LIKE ANALYTICS GLANCE BOARD */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="sh-stat-card border border-slate-200 bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] font-bold uppercase tracking-wider">Total Tasks</span>
            <ClipboardList className="w-4 h-4 text-slate-400" />
          </div>
          <div className="sh-stat-value text-3xl font-extrabold text-slate-900 tracking-tight mt-1">{totalCount}</div>
          <div className="text-[10px] text-slate-450 mt-1">Comprehensive operation registry</div>
        </div>

        <div className="sh-stat-card border border-slate-200 bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] font-bold uppercase tracking-wider">Completed</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="sh-stat-value text-3xl font-extrabold text-emerald-650 tracking-tight mt-1">{completedCount}</div>
          {/* Inline progress line inside details */}
          <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>Overall completion rate</span>
            <span>{progressPercent}%</span>
          </div>
        </div>

        <div className="sh-stat-card border border-slate-200 bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] font-bold uppercase tracking-wider">Overdue</span>
            <AlertCircle className="w-4 h-4 text-rose-500 animate-pulse" />
          </div>
          <div className="sh-stat-value text-3xl font-extrabold text-rose-650 tracking-tight mt-1">{overdueCount}</div>
          <div className="text-[10px] text-rose-600 font-semibold bg-rose-50 px-1.5 py-0.5 rounded inline-block mt-1">
            {overdueCount > 0 ? "Requires immediate actions" : "No overdue risks detected"}
          </div>
        </div>

        <div className="sh-stat-card border border-slate-200 bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] font-bold uppercase tracking-wider">Pending Active</span>
            <Clock className="w-4 h-4 text-amber-500" />
          </div>
          <div className="sh-stat-value text-3xl font-extrabold text-slate-800 tracking-tight mt-1">{pendingCount}</div>
          <div className="text-[10px] text-slate-450 mt-1">Ongoing roadmap streams</div>
        </div>
      </div>

      {/* CRISIS ALERT NOTIFICATION BANNER */}
      {overdueCount > 0 && (
        <div className="flex gap-3 bg-red-50/70 border border-red-100 text-red-900 p-4 rounded-xl items-start">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold">Action items are lagging behind schedule!</h4>
            <p className="text-xs text-red-700 mt-0.5">There are currently {overdueCount} task(s) past their target deadlines. Resolve or push back dates on these tasks to ensure consistent execution stats.</p>
          </div>
        </div>
      )}

      {/* INTERACTIVE COMPOSER OVERLAY CARD */}
      {showAddForm && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm ring-4 ring-slate-100/50 transition-all duration-300">
          <div className="flex justify-between items-center pb-4 mb-4 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900 font-display flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-slate-700" /> Ensure Next Task Metric
            </h2>
            <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={handleAddTask} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Task Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                placeholder="e.g., Secure local vendor permissions for the main lobby"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                className="w-full text-sm font-medium px-4 py-2.5 border border-slate-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Area / Category</label>
              <select
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                className="w-full text-sm font-medium px-3 py-2.5 bg-white border border-slate-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Priority Level</label>
              <select
                value={newPriority}
                onChange={e => setNewPriority(e.target.value)}
                className="w-full text-sm font-medium px-3 py-2.5 bg-white border border-slate-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="high">🔴 High Urgency</option>
                <option value="medium">🟡 Medium Standard</option>
                <option value="low">🟢 Low Option</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Assigned Owner</label>
              <input
                type="text"
                placeholder="e.g., Jane Cooper"
                value={newAssignee}
                onChange={e => setNewAssignee(e.target.value)}
                className="w-full text-sm font-medium px-4 py-2 border border-slate-250 rounded-lg focus:outline-none focus:focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Target Deadline Date
              </label>
              <input
                type="date"
                required
                value={newDueDate}
                onChange={e => setNewDueDate(e.target.value)}
                className="w-full text-sm font-medium px-4 py-2 border border-slate-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <div className="md:col-span-1 flex items-end">
              <button
                type="submit"
                disabled={addingTask}
                className="w-full py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-all shadow-sm text-sm active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {addingTask && <Loader2 size={14} className="animate-spin" />}
                {addingTask ? 'Saving…' : 'Insert Task'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FILTER CONTROL BAR */}
      <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-150 flex flex-col xl:flex-row items-center gap-3">
        {/* Search */}
        <div className="relative w-full xl:w-72">
          <Search className="w-4 h-4 text-slate-450 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search tasks, owner..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-1.5 text-xs font-medium bg-white border border-slate-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Filters Group */}
        <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto xl:ml-auto">
          {/* Status buttons */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 border border-slate-200">
            {[
              { id: 'all', label: 'All' },
              { id: 'pending', label: 'Pending' },
              { id: 'overdue', label: 'Overdue' },
              { id: 'done', label: 'Completed' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setStatusFilter(item.id)}
                className={`px-3 py-1 text-[10px] sm:text-xs font-bold rounded-md transition-all ${
                  statusFilter === item.id 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-650 hover:text-slate-900'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Priority Select */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:inline">Priority:</span>
            <select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg text-xs font-semibold px-2 py-1 focus:outline-none"
            >
              <option value="all">Priority (All)</option>
              <option value="high">🔴 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">🟢 Low</option>
            </select>
          </div>

          {/* Category Select */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:inline">Category:</span>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg text-xs font-semibold px-2 py-1 focus:outline-none"
            >
              <option value="all">Category (All)</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 bg-white rounded-2xl">
          <CheckCircle2 className="w-10 h-10 text-slate-350 mx-auto mb-2" />
          <p className="text-base font-bold text-slate-700">No matching tasks</p>
          <p className="text-xs text-slate-450 mt-1 max-w-sm mx-auto">Try widening your search terms, clearing the active category filters, or add a prompt task to get started.</p>
          {(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setPriorityFilter('all');
                setCategoryFilter('all');
              }}
              className="mt-4 text-xs font-bold text-slate-900 border-b border-slate-900 hover:opacity-85"
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        /* TASK LIST CONTAINER */
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50/20 flex justify-between items-center">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Currently Showing {filteredTasks.length} task{filteredTasks.length === 1 ? '' : 's'}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {filteredTasks.map(task => (
              <div 
                key={task.id} 
                className={`p-4 sm:p-5 flex items-start gap-4 transition-all hover:bg-slate-50/50 ${
                  task.status === 'done' ? 'bg-slate-50/20' : ''
                }`}
              >
                {/* State toggle check */}
                <button
                  onClick={() => handleToggleStatus(task)}
                  disabled={busyIds.has(task.id)}
                  className="text-slate-300 hover:text-slate-900 transition-colors mt-0.5 shrink-0 focus:outline-none disabled:opacity-50"
                  title={task.status === 'done' ? 'Mark continuous pending' : 'Mark done'}
                >
                  {busyIds.has(task.id) ? (
                    <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                  ) : task.status === 'done' ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 hover:scale-105 transition-transform" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-350 hover:text-slate-800 hover:scale-105 transition-transform" />
                  )}
                </button>

                {/* Task Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className={`text-sm sm:text-base font-bold tracking-tight text-slate-900 transition-all ${
                      task.status === 'done' ? 'line-through text-slate-400 font-medium' : ''
                    }`}>
                      {task.title}
                    </h3>
                    
                    {/* Status badges */}
                    {task.isOverdue && (
                      <span className="px-2 py-0.5 bg-red-50 text-red-700 border border-red-100 rounded text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5">
                        <AlertCircle className="w-2.5 h-2.5" /> OVERDUE
                      </span>
                    )}

                    {task.status === 'done' && !task.isOverdue && (
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-bold uppercase tracking-wider">
                        COMPLETED
                      </span>
                    )}
                  </div>

                  {/* Task Meta details row */}
                  <div className="flex flex-wrap items-center text-[11px] text-slate-500 gap-x-4 gap-y-1.5 mt-2 font-medium">
                    {/* Priority badge */}
                    <span className="flex items-center gap-1 font-bold">
                      {task.priority === 'high' && (
                        <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-800 text-[10px] border border-red-100">🔴 High</span>
                      )}
                      {task.priority === 'medium' && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[10px] border border-amber-100">🟡 Medium</span>
                      )}
                      {task.priority === 'low' && (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 text-[10px] border border-emerald-100">🟢 Low</span>
                      )}
                    </span>

                    {/* Category Label */}
                    <span className="flex items-center gap-1 bg-slate-100/80 px-2 py-0.5 rounded text-slate-700 font-bold border border-slate-200">
                      <Tag className="w-3 h-3 text-slate-400" />
                      {task.category}
                    </span>

                    {/* Due Date Indicator */}
                    <span className={`flex items-center gap-1 ${task.isOverdue ? 'text-red-650 font-bold' : ''}`}>
                      <Calendar className="w-3 h-3 shrink-0" />
                      Due Date: {new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>

                    {/* Assignee Indicator */}
                    <span className="flex items-center gap-1 text-slate-650">
                      <User className="w-3 h-3 text-slate-400" />
                      Owner: <strong className="text-slate-800">{task.assignee}</strong>
                    </span>
                  </div>
                </div>

                {/* Actions Button */}
                <button
                  onClick={() => handleDeleteTask(task.id)}
                  disabled={busyIds.has(task.id)}
                  className="p-1 text-slate-350 hover:text-red-600 rounded-lg hover:bg-slate-100 shrink-0 self-center transition-colors disabled:opacity-50"
                  title="Purge Task"
                >
                  {busyIds.has(task.id)
                    ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
